import { AlertCircleSolidIcon } from '@bfe/moly-icons';
import { useToggle } from 'ahooks';
import { Card, message, Space, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { lazy, Suspense, useCallback, useState } from 'react';

import BybitSearchList from '@/components/SearchTablePage/BybitSearchList';
import BybitTable from '@/components/SearchTablePage/BybitTable';
import { BybitSearchListRef } from '@/components/SearchTablePage/utils/commonInterface';
import { IAjaxResult } from '@/components/SearchTablePage/utils/commonInterface';
import useIslamicSubAccount from '@/hooks/useIslamicSubAccount';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useI18nContext } from '@/i18n/I18n';
import { apiList, apiSubList } from '@/services/apiManagement';
import { IPermission } from '@/services/types/apiManagement';
import {
  IApiManagementRecord,
  IParentApiManagement,
} from '@/services/types/apiManagement';
import { SubMemberType } from '@/utils/constants/sub-account';
import { ApiManagementListDataFail } from '@/utils/trace-list/openApi';

import { ApiSymbolBtn } from '../../comps/ApiSymbol';
import { BindIpBtn } from '../../comps/BindIpBtn';
import { DeleteApiBtn } from '../../comps/DeleteApiBtn';
import { EditApiBtn } from '../../comps/EditApiBtn';
import { useHelper } from '../../helper';
import styles from '../../index.module.less';
import SecretPwd from './SecretPwd';

const BatchBindIp = lazy(() => import('../../comps/BatchBindIp'));

const { Paragraph } = Typography;

interface IProps {
  subAccountId: string | number | null;
  isFromSubAccount: boolean;
  searchListRef: React.MutableRefObject<
    BybitSearchListRef<IApiManagementRecord> | undefined
  >;
  refreshTable: () => void;
  hasGoogle2fa: () => boolean;
  isSubAccountLogin: boolean;
  loading: boolean;
  apiRequestCallback: (res: any) => void;
  isCopperSubAccount: boolean;
  memberType?: string | null;
}

const ApiManagementList = ({
  subAccountId,
  isFromSubAccount,
  searchListRef,
  refreshTable,
  hasGoogle2fa,
  isSubAccountLogin,
  loading,
  apiRequestCallback,
  isCopperSubAccount,
  memberType,
}: IProps) => {
  const [$t, lang] = useI18nContext();

  const [isBatchBindIp, { toggle: toggleBatchBindIp }] = useToggle(false);
  const [selectedRowApi, setSelectedRowApi] = useState<IApiManagementRecord[]>(
    [],
  );
  const [showBatchBtn, setShowBatchBtn] = useState<boolean>(false);

  const { dealPermission } = useHelper();

  const { isDemoAccount } = useUserProfile();

  const [isBan, setIsBan] = useState(false);

  const { isIslamicSubAccount } = useIslamicSubAccount({ pageName: 'openApi' });

  const renderPermission = useCallback(
    (
      isSubAccount: boolean,
      permissions: IPermission,
      record: IApiManagementRecord,
    ) => {
      const { readOnly, read_only } = record as any;
      return dealPermission(permissions, isSubAccount, readOnly || read_only);
    },
    [lang, dealPermission],
  );

  const getColumns = (
    isFromSubAccount = false,
    subAccountId: string | number | null,
    isBatchBindIp: boolean,
    refreshTable: () => void,
    hasGoogle2fa: () => boolean,
    memberType?: string | null,
  ) => {
    const columns: ColumnsType<IApiManagementRecord> = [
      {
        title: `${$t('api remark')}/${$t('addSecretKey.p6')}`,
        dataIndex: 'note',
        width: 240,
        render: (note, record) => {
          return (
            <div className={styles.tableNote}>
              <span title={note}>{note}</span>
              {record.flag ? (
                <i
                  className={classNames(styles.apiTag, {
                    [styles.apiTagHmac]: record.flag === 'hmac',
                    [styles.apiTagRsa]: record.flag === 'rsa',
                  })}
                >
                  {record.flag.toUpperCase()}
                </i>
              ) : null}
            </div>
          );
        },
      },
      {
        title: $t('addSecretKey.p1'),
        dataIndex: 'type',
        width: 120,
        ellipsis: true,
        render: (type) => {
          if (Number(type) === 2) {
            return (
              <div style={{ paddingRight: '20px' }}>
                {$t('addSecretKey.p3')}
              </div>
            );
          }
          return (
            <div style={{ paddingRight: '20px' }}>{$t('addSecretKey.p2')}</div>
          );
        },
      },
      {
        title: `API${$t('Key')}`,
        dataIndex: 'api_key',
        width: 240,
        render: (apiKey) => {
          return (
            <Paragraph
              copyable={{
                text: apiKey,
                icon: [
                  <i className="byIcon icon-u_book-medical" key="copy-icon" />,
                  <i className="byIcon icon-wancheng" key="copied-icon" />,
                ],
                tooltips: ['', ''],
                onCopy: () => {
                  message.success($t('Address Copied'));
                },
              }}
            >
              <span style={{ color: 'var(--bds-gray-t1-title)' }}>
                {apiKey}
              </span>
            </Paragraph>
          );
        },
      },
      {
        title: $t('Secret Key'),
        dataIndex: 'secret',
        ellipsis: true,
        width: 150,
        render: (secret, record) => {
          return <SecretPwd record={record as IParentApiManagement} />;
        },
      },
      {
        title: $t('Permission'),
        dataIndex: 'permissions',
        width: 300,
        render: (permissions, record) => {
          return (
            <div style={{ paddingRight: '20px' }}>
              {renderPermission(!!subAccountId, permissions, record)}
            </div>
          );
        },
      },
      {
        title: $t('Bind IP address'),
        dataIndex: 'ips',
        width: 160,
        render: (ips, record) => {
          const { api_key, isCust } = record as IParentApiManagement;
          const { type } = record;

          if (type === 2 || type === 0 || type === 'API_TYPE_APPLICATION') {
            if (ips === '*') {
              return '--';
            }
            return <span>{ips}</span>;
          }

          return (
            <BindIpBtn
              selectedApis={[record]}
              apiKey={api_key}
              ips={ips}
              updateIps={refreshTable}
              hasGoogle2fa={hasGoogle2fa}
              subAccountId={subAccountId as string}
              isFromSubAccount={isFromSubAccount}
              disabled={isCust || isBatchBindIp}
            />
          );
        },
      },
      {
        title: $t('Created Time'),
        dataIndex: 'created_at',
        width: 180,
        render: (time) => {
          return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
        },
      },
      {
        title: $t('apiDeadlineDay'),
        dataIndex: 'deadline_day',
        width: 180,
        render: (deadlineDay) => {
          if (deadlineDay === -2) {
            return $t('AddApi.p8');
          }
          if (deadlineDay === -1) {
            return (
              <div style={{ color: 'var(--bds-red-700-normal)' }}>
                {$t('apiExpired')}
              </div>
            );
          }
          return deadlineDay;
        },
      },

      {
        title: $t('Action'),
        dataIndex: 'action',
        className: 'bybit-action',
        fixed: 'right',
        width: 80,
        render: (_, apiManagementRecord: IApiManagementRecord) => {
          const { id, type, api_key, isCust } =
            apiManagementRecord as IParentApiManagement;

          const isThirdApplication =
            type === 2 || type === 0 || type === 'API_TYPE_APPLICATION';
          // 隐藏币对设置按钮
          // 模拟盘和母账号操作伊斯兰子账号API
          const hideApiSymbolBtn =
            isDemoAccount ||
            (isFromSubAccount &&
              (isIslamicSubAccount ||
                memberType === SubMemberType.EARN_FUND_ACCOUNT));
          return (
            <Space size={8}>
              {!hideApiSymbolBtn && (
                <ApiSymbolBtn
                  apikey={api_key}
                  subAccountId={subAccountId as string}
                  isFromSubAccount={isFromSubAccount}
                  apiManagementId={id}
                  refreshTable={refreshTable}
                  disabled={isCust}
                />
              )}
              <DeleteApiBtn
                subAccountId={subAccountId as string}
                isFromSubAccount={isFromSubAccount}
                apiManagementId={id}
                refreshTable={refreshTable}
                disabled={isCust}
              />
              {!isThirdApplication &&
                !isCust &&
                (!subAccountId || isFromSubAccount || isDemoAccount) && (
                  <EditApiBtn
                    apiManagementId={id}
                    subAccountId={subAccountId as string}
                    isFromSubAccount={isFromSubAccount}
                    hasGoogle2fa={hasGoogle2fa}
                    isCopperSubAccount={isCopperSubAccount}
                  />
                )}
            </Space>
          );
        },
      },
    ];
    if (isBatchBindIp) {
      columns.pop();
    }
    return columns;
  };

  const onSuccess = (rs: IAjaxResult) => {
    const result = rs.result;
    if (subAccountId) {
      if (isSubAccountLogin) {
        setShowBatchBtn(false);
      } else {
        setShowBatchBtn(!!result?.records?.length);
      }
      result.records.forEach((item: any) => {
        item.ips = `${item.ips}`;
        item.created_at = item.createdAt;
        item.expired_at = item.expiredAt;
        item.api_key = item.apiKey;

        item.type = item.type === 'API_TYPE_PERSONAL' ? 1 : 2;
      });
    } else {
      setShowBatchBtn(!!result?.length);
      result.forEach((item: any) => {
        item.ips = `${item.ips}`;
      });
    }
  };
  const getSelectionProps = () => {
    if (!isBatchBindIp) {
      return {};
    }
    return {
      scroll: { x: '600px' },
      rowSelection: {
        getCheckboxProps: (record: IApiManagementRecord) => ({
          disabled:
            record?.type === 2 ||
            record?.type === 0 ||
            record?.type === 'API_TYPE_APPLICATION' ||
            record.isCust,
        }),
        onChange: (_: React.Key[], selectedRows: IApiManagementRecord[]) => {
          setSelectedRowApi(selectedRows);
        },
      },
    };
  };

  const getApi = async () => {
    try {
      if (subAccountId) {
        const res = (await apiSubList(subAccountId)) as any;
        setIsBan(res?.result?.is_ban === 1);
        apiRequestCallback(res?.result);
        return res;
      } else {
        const res = (await apiList()) as any;
        setIsBan(res?.result?.[0]?.is_ban === 1);
        apiRequestCallback(res);
        return res;
      }
    } catch (error) {
      ApiManagementListDataFail(error);
      return error;
    }
  };

  const getSearchTableProps = () => {
    return {
      searchListRef,
      rowKey: 'id',
      columns: getColumns(
        isFromSubAccount,
        subAccountId,
        isBatchBindIp,
        refreshTable,
        hasGoogle2fa,
        memberType,
      ),
      dataSourceKey: subAccountId ? 'result.records' : 'result',
      hideSearch: true,
      onSuccess,
      getApi,
      footer: () => null,
      tableProps: getSelectionProps(),
      className: `api-management-list ${
        isBan ? 'api-management-list-ban' : ''
      }`,
    };
  };
  const getTableProps: any = () => {
    return {
      columns: [
        getColumns(
          isFromSubAccount,
          subAccountId,
          isBatchBindIp,
          refreshTable,
          hasGoogle2fa,
        ),
      ],
      dataSource: [],
      rowKey: 'id',
      loading: loading,
      footer: () => null,
    };
  };
  const onCancel = () => {
    toggleBatchBindIp();
    setSelectedRowApi([]);
  };

  return (
    <Card
      className="bybit-card-with-title"
      bordered={false}
      style={{ maxWidth: '100%' }}
    >
      <div
        className="flex"
        style={{
          justifyContent: 'flex-end',
          marginBottom: '16px',
          alignItems: 'center',
        }}
      >
        <div style={{ fontSize: '12px', fontWeight: 400 }}>
          {$t('apiManagement list')}
        </div>
        <div style={{ flex: 1, textAlign: 'right' }}>
          <Space>
            <Suspense fallback="">
              {!loading && (showBatchBtn || isDemoAccount) && (
                <BatchBindIp
                  selectedRowApi={selectedRowApi}
                  updateIps={refreshTable}
                  hasGoogle2fa={hasGoogle2fa}
                  subAccountId={subAccountId as string}
                  isFromSubAccount={isFromSubAccount}
                  isBatchBindIp={isBatchBindIp}
                  toggleBatchBindIp={onCancel}
                  disabled={isBan}
                />
              )}
            </Suspense>
          </Space>
        </div>
      </div>

      {isBan && (
        <div className="api-manage-list-ban-tip">
          <AlertCircleSolidIcon />
          {$t('banAccountApiDisableTip')}
        </div>
      )}

      {loading ? (
        <>
          <BybitTable<never> {...getTableProps()} />
        </>
      ) : (
        <>
          <BybitSearchList<IApiManagementRecord> {...getSearchTableProps()} />
        </>
      )}
    </Card>
  );
};

export default ApiManagementList;
